import React from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Video, Languages, CreditCard, Mic, PlayCircle, GraduationCap, UserCheck, ArrowRight } from "lucide-react";
import { Tx } from "../context/AutoTranslateContext";
import TranslationNotice from "../components/TranslationNotice";

const Step = ({ n, Icon, title, body }) => (
  <div data-testid={`hiw-step-${n}`} className="clay-surface rounded-3xl p-6 flex gap-4">
    <div className="flex flex-col items-center">
      <span className="w-9 h-9 rounded-full bg-forest text-sand flex items-center justify-center text-sm font-bold">{n}</span>
    </div>
    <div>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-5 h-5 text-gold" />
        <h3 className="font-heading text-lg text-forest font-semibold"><Tx>{title}</Tx></h3>
      </div>
      <p className="text-sm text-mute leading-relaxed"><Tx>{body}</Tx></p>
    </div>
  </div>
);

const HowItWorks = () => (
  <div data-testid="how-it-works-page" className="max-w-5xl mx-auto px-4 sm:px-8 py-12">
    <div className="flex items-center gap-2 text-xs text-mute mb-6">
      <Link to="/academy" className="hover:text-forest"><Tx>Academy</Tx></Link>
      <span>/</span><span className="text-forest"><Tx>How it works</Tx></span>
    </div>

    <div className="text-center mb-10">
      <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3"><Tx>Step by step</Tx></p>
      <h1 className="font-heading text-4xl sm:text-5xl text-forest font-bold mb-4"><Tx>How the Academy works</Tx></h1>
      <p className="text-mute leading-relaxed max-w-2xl mx-auto">
        <Tx>Learning the Qur'an and Arabic with a live tutor anywhere in the world — made simple. Here's exactly what happens, for both students and teachers.</Tx>
      </p>
    </div>

    {/* Explainer video placeholder */}
    <div data-testid="hiw-video" className="rounded-3xl overflow-hidden bg-forest text-sand mb-12 relative aspect-video flex items-center justify-center">
      <div className="absolute inset-0 bg-islamic-pattern opacity-10" />
      <div className="relative text-center px-6">
        <PlayCircle className="w-14 h-14 text-gold mx-auto mb-3" />
        <p className="font-heading text-xl font-semibold mb-1"><Tx>Watch the 90-second explainer</Tx></p>
        <p className="text-xs text-sand/70 max-w-md mx-auto">
          <Tx>Short walkthrough: choosing a tutor, how your timezone is handled, the 30-minute video class, and the live AI translation in action. (Video coming soon.)</Tx>
        </p>
      </div>
    </div>

    <TranslationNotice className="mb-12" />

    {/* For students */}
    <h2 className="font-heading text-2xl text-forest font-semibold mb-5 flex items-center gap-2">
      <GraduationCap className="w-5 h-5 text-gold" /> <Tx>For students</Tx>
    </h2>
    <div className="grid sm:grid-cols-2 gap-4 mb-12">
      <Step n={1} Icon={Mic} title="Try the free AI Pronunciation Coach" body="Record yourself reciting one ayah. This quick, free step unlocks tutor booking and helps match you to the right teacher." />
      <Step n={2} Icon={CalendarClock} title="Pick a tutor & a time in YOUR timezone" body="Every tutor publishes weekly availability in their own country's time. We automatically convert each slot to your local timezone — no maths, no confusion." />
      <Step n={3} Icon={CreditCard} title="Book & pay securely" body="Choose a plan (£8 single · £25 for 4 · £40 for 8 lessons a month) and pay by card. You instantly receive your private video-room link." />
      <Step n={4} Icon={Video} title="Join your 30-minute live class" body="At the scheduled time, open the in-app video room — a private, encrypted call. Each session is a focused 30 minutes." />
      <Step n={5} Icon={Languages} title="Understand everything — live AI translation" body="Turn on live captions and voice translation. Arabic the tutor speaks appears instantly as translated text (and voice) in your language." />
      <Step n={6} Icon={UserCheck} title="Rate your tutor" body="After the session, leave a 1–5 star rating and review. Your feedback helps other students choose and keeps quality high." />
    </div>

    {/* For teachers */}
    <h2 className="font-heading text-2xl text-forest font-semibold mb-5 flex items-center gap-2">
      <UserCheck className="w-5 h-5 text-gold" /> <Tx>For teachers</Tx>
    </h2>
    <div className="grid sm:grid-cols-2 gap-4 mb-12">
      <Step n={1} Icon={GraduationCap} title="Apply with a recitation sample" body="Fill in your profile, select the languages you teach in, set your weekly availability in your own timezone, and record a short recitation." />
      <Step n={2} Icon={UserCheck} title="Manual review within 3 working days" body="Our management personally reviews every application. You'll be notified of the decision by WhatsApp AND email the moment we approve or decline." />
      <Step n={3} Icon={Video} title="Teach your 30-minute sessions" body="Once approved, students can book you. Join the same private video room — teach comfortably in Arabic; the AI bridges the language gap." />
      <Step n={4} Icon={Languages} title="No language barrier, ever" body="Even if your students speak no Arabic, live AI voice & text translation lets you teach naturally while they follow along in their language." />
    </div>

    <div className="text-center">
      <Link to="/academy/tutors" data-testid="hiw-cta" className="inline-flex items-center gap-2 bg-forest text-sand hover:bg-sage rounded-full px-7 py-4 text-sm font-medium transition-all hover:-translate-y-0.5">
        <Tx>Browse tutors & book</Tx> <ArrowRight className="w-4 h-4 rtl:rotate-180" />
      </Link>
      <div className="mt-3">
        <Link to="/academy/translate-demo" className="text-xs text-forest hover:underline">
          <Tx>Or try the live AI translation demo →</Tx>
        </Link>
      </div>
    </div>
  </div>
);

export default HowItWorks;
